/**
 * art-terms.js — progressive enhancement for the art buying directory.
 *
 * Reads the glossary terms from an inline JSON island (rendered by
 * snippets/art-directory-terms-data.liquid from the custom.art_directory_terms
 * metafield) and quietly decorates the first mention of each term inside
 * [data-terms-scope] containers with a hover/focus tooltip that links to the
 * full definition on /pages/art-buying-terms.
 *
 * With JS off the page is untouched and complete (the glossary link lines are
 * the no-JS path). Nothing here wraps text inside links, headings or buttons,
 * and the whole page is capped at MAX_TERMS underlines so it never becomes
 * underline soup.
 */
(() => {
  const GLOSSARY_URL = '/pages/art-buying-terms';
  const MAX_TERMS = 15;
  const TOOLTIP_ID = 'art-term-tooltip';

  const island = document.querySelector('script[type="application/json"][data-art-terms]');
  const scopes = document.querySelectorAll('[data-terms-scope]');
  if (!island || scopes.length === 0) return;

  let terms;
  try {
    terms = JSON.parse(island.textContent);
  } catch (e) {
    return;
  }
  if (!Array.isArray(terms) || terms.length === 0) return;

  // Flatten to a phrase list, longest phrase first so specific phrases
  // ("print resolution") win over anything shorter they might contain.
  const shortById = {};
  const phraseList = [];
  for (const t of terms) {
    if (!t || !t.id) continue;
    shortById[t.id] = t.short || '';
    const matches = Array.isArray(t.match) && t.match.length ? t.match : [t.id];
    for (const m of matches) {
      if (m && m.trim()) phraseList.push({ id: t.id, phrase: m.trim().toLowerCase() });
    }
  }
  phraseList.sort((a, b) => b.phrase.length - a.phrase.length);

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const SKIP_TAGS = new Set(['A', 'BUTTON', 'SUMMARY', 'SCRIPT', 'STYLE', 'NOSCRIPT', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

  function isSkippable(node) {
    let el = node.parentElement;
    while (el) {
      if (SKIP_TAGS.has(el.tagName)) return true;
      if (el.classList && el.classList.contains('art-term')) return true;
      el = el.parentElement;
    }
    return false;
  }

  function collectTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (isSkippable(node)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const nodes = [];
    let n;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }

  function wrapMatch(textNode, index, length, id) {
    const text = textNode.nodeValue;
    const before = text.slice(0, index);
    const matchText = text.slice(index, index + length);
    const after = text.slice(index + length);

    const span = document.createElement('span');
    span.className = 'art-term';
    span.textContent = matchText;
    span.tabIndex = 0;
    span.setAttribute('role', 'button');
    span.dataset.termId = id;
    span.setAttribute('aria-label', matchText + ', art buying term');

    const frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    frag.appendChild(span);
    if (after) frag.appendChild(document.createTextNode(after));
    textNode.parentNode.replaceChild(frag, textNode);
    return span;
  }

  const wrappedSpans = [];
  let total = 0;

  for (const scope of scopes) {
    if (total >= MAX_TERMS) break;
    const usedIds = new Set();
    // Re-collect after each wrap, since wrapping mutates the DOM under us.
    for (const { id, phrase } of phraseList) {
      if (total >= MAX_TERMS) break;
      if (usedIds.has(id)) continue;
      const re = new RegExp('\\b' + escapeRegExp(phrase) + '\\b', 'i');
      const nodes = collectTextNodes(scope);
      let done = false;
      for (const node of nodes) {
        const m = re.exec(node.nodeValue);
        if (m) {
          const span = wrapMatch(node, m.index, m[0].length, id);
          wrappedSpans.push(span);
          usedIds.add(id);
          total++;
          done = true;
          break;
        }
      }
      if (done) continue;
    }
  }

  if (wrappedSpans.length === 0) return;

  // One shared tooltip for the whole page.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tooltip = document.createElement('div');
  tooltip.className = 'art-term-tooltip' + (reduceMotion ? ' art-term-tooltip--no-motion' : '');
  tooltip.id = TOOLTIP_ID;
  tooltip.setAttribute('role', 'tooltip');
  tooltip.hidden = true;
  const tipText = document.createElement('span');
  tipText.className = 'art-term-tooltip__text';
  const tipLink = document.createElement('a');
  tipLink.className = 'art-term-tooltip__link';
  tipLink.textContent = 'Full explanation';
  tooltip.appendChild(tipText);
  tooltip.appendChild(tipLink);
  document.body.appendChild(tooltip);

  let activeTerm = null;
  let hideTimer = null;
  let lastPointerTouch = false;

  function positionTooltip(term) {
    const rect = term.getBoundingClientRect();
    tooltip.style.visibility = 'hidden';
    tooltip.hidden = false;
    const tipRect = tooltip.getBoundingClientRect();
    let left = rect.left;
    const maxLeft = window.innerWidth - tipRect.width - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    if (left < 12) left = 12;
    let top = rect.bottom + 8;
    // Flip above if it would fall off the bottom of the viewport.
    if (top + tipRect.height > window.innerHeight - 12 && rect.top - tipRect.height - 8 > 12) {
      top = rect.top - tipRect.height - 8;
    }
    tooltip.style.left = left + 'px';
    tooltip.style.top = top + 'px';
    tooltip.style.visibility = 'visible';
  }

  function showFor(term) {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    if (activeTerm && activeTerm !== term) activeTerm.removeAttribute('aria-describedby');
    activeTerm = term;
    tipText.textContent = shortById[term.dataset.termId] || '';
    tipLink.href = GLOSSARY_URL + '#' + term.dataset.termId;
    term.setAttribute('aria-describedby', TOOLTIP_ID);
    positionTooltip(term);
  }

  function hide() {
    tooltip.hidden = true;
    if (activeTerm) activeTerm.removeAttribute('aria-describedby');
    activeTerm = null;
  }

  function scheduleHide() {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, 160);
  }

  function goToGlossary(term) {
    window.location.href = GLOSSARY_URL + '#' + term.dataset.termId;
  }

  for (const span of wrappedSpans) {
    span.addEventListener('mouseenter', () => showFor(span));
    span.addEventListener('mouseleave', scheduleHide);
    span.addEventListener('focus', () => showFor(span));
    span.addEventListener('blur', scheduleHide);

    span.addEventListener('pointerdown', (e) => { lastPointerTouch = e.pointerType === 'touch'; });

    span.addEventListener('click', (e) => {
      if (lastPointerTouch) {
        // First tap reveals the tooltip; a second tap on the shown term follows through.
        if (activeTerm === span && !tooltip.hidden) {
          goToGlossary(span);
        } else {
          e.preventDefault();
          showFor(span);
        }
      } else {
        goToGlossary(span);
      }
    });

    span.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        goToGlossary(span);
      } else if (e.key === 'Escape') {
        hide();
      }
    });
  }

  tooltip.addEventListener('mouseenter', () => { if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } });
  tooltip.addEventListener('mouseleave', scheduleHide);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeTerm) {
      const t = activeTerm;
      hide();
      t.focus();
    }
  });

  // A tap or click anywhere else dismisses an open tooltip.
  document.addEventListener('pointerdown', (e) => {
    if (activeTerm && !e.target.closest('.art-term') && !e.target.closest('.art-term-tooltip')) hide();
  });

  window.addEventListener('scroll', () => { if (activeTerm) hide(); }, { passive: true });
})();
